import moment from 'moment'
import { ReactElement, ReactNode } from 'react'
import { SectionCard } from 'app/components/SectionCard'
import { Tooltip } from 'app/components/Tooltip'
import { Button } from 'app/components/ui/button'
import { intercomEnabled } from 'app/intercom'
import {
  AgentStatusLine,
  AgentStatusTone,
  STATUS_HISTORY_DAYS,
  StatusHistoryDay,
  TONE_WORDS,
  buildSampleStatusHistory,
  describeOutageWindow,
} from 'app/lib/aiAgentOverview'
import { cn } from 'app/lib/utils'

const STATUS_DOT: Record<AgentStatusTone, string> = {
  online: `bg-success-default`,
  redirecting: `bg-info-default`,
  interrupted: `bg-danger-default`,
  offline: `bg-surface-strong`,
}

/** Days in the strip: an offline day takes the muted text gray, which reads in both themes. */
const STATUS_BAR: Record<AgentStatusTone, string> = {
  ...STATUS_DOT,
  offline: `bg-current text-minimal`,
}

const TONE_BADGE: Record<AgentStatusTone, string> = {
  online: `bg-success-minimal text-success-strong`,
  redirecting: `bg-info-minimal text-info-strong`,
  interrupted: `bg-danger-minimal text-danger-strong`,
  offline: `bg-surface-muted text-default`,
}

/** An agent's status as a dot and its words, colored by tone; `label` replaces the words. */
export function AgentStatusBadge({ tone, label }: { tone: AgentStatusTone; label?: string }): ReactElement {
  return (
    <span
      className={cn(
        `inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-label-sm whitespace-nowrap`,
        TONE_BADGE[tone],
      )}
    >
      <span className={cn(`h-1.5 w-1.5 rounded-full`, STATUS_DOT[tone])} />
      {label ?? TONE_WORDS[tone]}
    </span>
  )
}

type Channel = `voice` | `sms`

const CHANNEL_NAME: Record<Channel, string> = { voice: `Voice Agent`, sms: `SMS Agent` }

function ColumnHeader({ name, children }: { name: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-body-md font-semibold text-strong">{name}</span>
      {children}
    </div>
  )
}

function StatusColumn({
  channel,
  line,
  history,
}: {
  channel: Channel
  line: AgentStatusLine
  history: StatusHistoryDay[]
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-4 p-4" aria-label={line.label}>
      <ColumnHeader name={CHANNEL_NAME[channel]}>
        <AgentStatusBadge tone={line.tone} />
      </ColumnHeader>
      <div className="flex h-8 gap-1">
        {history.map((day) => (
          <Tooltip
            key={day.date}
            placement="top"
            className="flex min-w-0 flex-1"
            message={
              <div className="flex flex-col gap-0.5 p-1">
                <p className="text-body-xs inverse-text-default">{moment(day.date).format(`ddd, MMM D`)}</p>
                {day.outages.length === 0 ? (
                  <p className="text-ui-sm inverse-text-strong">{TONE_WORDS[day.tone]}</p>
                ) : (
                  <>
                    {day.outages.map((outage) => (
                      <p key={outage.fromMinute} className="text-ui-sm inverse-text-strong">
                        {TONE_WORDS[outage.tone]}
                        <span className="inverse-text-default"> · {describeOutageWindow(outage)}</span>
                      </p>
                    ))}
                    {day.outages.some((outage) => outage.fromMinute > 0 || outage.toMinute < 24 * 60) && (
                      <p className="text-body-xs inverse-text-default">Online the rest of the day</p>
                    )}
                  </>
                )}
              </div>
            }
          >
            <div className={cn(`w-full rounded-xs transition-opacity hover:opacity-70`, STATUS_BAR[day.tone])} />
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

/**
 * The SMS column for a practice without the SMS Agent: the same shape as a status column, with a
 * faded strip and a way to ask for it in place of the badge. The ask opens Intercom with a message
 * already written, so where Intercom does not load the badge just says it is not included.
 */
function SmsUpgradeColumn({ practiceName }: { practiceName: string }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-4 p-4">
      <ColumnHeader name={CHANNEL_NAME.sms}>
        {intercomEnabled() ? (
          <Button
            type="button"
            variant="white"
            size="xs"
            onClick={() => window.Intercom?.(`showNewMessage`, `Hi! I'd like to add the SMS Agent to ${practiceName}.`)}
          >
            Add SMS Agent
          </Button>
        ) : (
          <span className="inline-flex items-center rounded-full bg-surface-muted px-2 py-0.5 text-label-sm text-default">
            Not included
          </span>
        )}
      </ColumnHeader>
      <div className="flex h-8 gap-1" aria-hidden="true">
        {Array.from({ length: STATUS_HISTORY_DAYS }, (_, i) => (
          <div key={i} className="flex-1 rounded-xs bg-surface-muted" />
        ))}
      </div>
    </div>
  )
}

/** Voice and SMS side by side: where each agent stands now, and the last month day by day. */
export function AiAgentStatusCells({
  voice,
  sms,
  practiceName,
}: {
  voice: AgentStatusLine
  sms: AgentStatusLine
  practiceName: string
}): ReactElement {
  return (
    <SectionCard
      attached
      title="Status"
      contentClassName="p-0 gap-0"
      headerButtons={
        <Tooltip message="Sample history until agent status is recorded. Today's status is real." placement="bottom">
          <span className="cursor-help text-body-xs text-subtle underline decoration-dotted underline-offset-2">
            Sample data
          </span>
        </Tooltip>
      }
    >
      <div className="grid grid-cols-1 divide-y divide-default md:grid-cols-2 md:divide-x md:divide-y-0">
        <StatusColumn channel="voice" line={voice} history={buildSampleStatusHistory(`voice`, voice.tone)} />
        {/* SMS is only ever off because the practice does not have the SMS Agent product. */}
        {sms.tone === `offline` ? (
          <SmsUpgradeColumn practiceName={practiceName} />
        ) : (
          <StatusColumn channel="sms" line={sms} history={buildSampleStatusHistory(`sms`, sms.tone)} />
        )}
      </div>
    </SectionCard>
  )
}
